'use client';

import { c as _c } from "react/compiler-runtime";
import { Calendar as CalendarIcon, NavArrowDown, PencilSquare, WarningTriangle } from '@byway/icons';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { createContext, useState } from 'react';
import { DatePicker as AriaDatePicker, type DatePickerProps as AriaDatePickerProps, DateInput, DateSegment, type DateValue, FieldError, Group, Button as RAC_Button, Label as RAC_Label } from 'react-aria-components';
import { Button } from '~/components/Button';
import { Calendar } from '~/components/Calendar';
import { Drawer } from '~/components/Drawer';
import { Popover } from '~/components/Popover';
import styles from './DatePicker.module.css';
type DatePickerProps = Omit<AriaDatePickerProps<DateValue>, 'minValue' | 'maxValue'> & {
  label?: string;
  errorMessage?: string;
  fullWidth?: boolean;
  labelInside?: boolean;
  children?: React.ReactNode;
  allowDirectInput?: boolean;
  showMonthYearDropdowns?: boolean;
  minValue: DateValue;
  maxValue: DateValue;
};
function makeGroupClickHandler(open: () => void) {
  return (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target.closest('[data-type]') && !target.closest('button')) {
      open();
    }
  };
}
function DatePicker(t0) {
  const $ = _c(69);
  let allowDirectInput;
  let children;
  let errorMessage;
  let isRequired;
  let label;
  let maxValue;
  let minValue;
  let props;
  let showMonthYearDropdowns;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      label,
      errorMessage,
      children,
      allowDirectInput,
      showMonthYearDropdowns,
      fullWidth: t1,
      labelInside: t2,
      isRequired,
      minValue,
      maxValue,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = allowDirectInput;
    $[2] = children;
    $[3] = errorMessage;
    $[4] = isRequired;
    $[5] = label;
    $[6] = maxValue;
    $[7] = minValue;
    $[8] = props;
    $[9] = showMonthYearDropdowns;
    $[10] = t1;
    $[11] = t2;
  } else {
    allowDirectInput = $[1];
    children = $[2];
    errorMessage = $[3];
    isRequired = $[4];
    label = $[5];
    maxValue = $[6];
    minValue = $[7];
    props = $[8];
    showMonthYearDropdowns = $[9];
    t1 = $[10];
    t2 = $[11];
  }
  const fullWidth = t1 === undefined ? true : t1;
  const labelInside = t2 === undefined ? true : t2;
  const t = useTranslations();
  const [isOpen, setIsOpen] = useState(false);
  let t3;
  if ($[12] !== allowDirectInput) {
    t3 = allowDirectInput ? makeGroupClickHandler(() => setIsOpen(true)) : undefined;
    $[12] = allowDirectInput;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const handleGroupClick = t3;
  const t4 = labelInside ? styles.datePicker : styles.datePickerOutside;
  const t5 = !fullWidth;
  let t6;
  if ($[14] !== t4 || $[15] !== t5) {
    t6 = clsx(t4, {
      [styles.fitContent]: t5
    });
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== allowDirectInput) {
    t7 = !allowDirectInput && <RAC_Button className={styles.triggerArea} />;
    $[17] = allowDirectInput;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== label || $[20] !== t) {
    t8 = label ?? t("content.forms.start_date");
    $[19] = label;
    $[20] = t;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== isRequired || $[23] !== labelInside) {
    t9 = !labelInside && isRequired && <span className={styles.required} aria-hidden="true">{" "}*</span>;
    $[22] = isRequired;
    $[23] = labelInside;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t8 || $[26] !== t9) {
    t10 = <RAC_Label className={styles.label}>{t8}{t9}</RAC_Label>;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const t11 = !labelInside;
  let t12;
  if ($[28] !== allowDirectInput || $[29] !== t11) {
    t12 = clsx(styles.group, {
      [styles.groupBordered]: t11,
      [styles.groupClickable]: allowDirectInput
    });
    $[28] = allowDirectInput;
    $[29] = t11;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== allowDirectInput) {
    t13 = <DateInput className={styles.dateInput}>{segment => <DateSegment segment={segment} className={allowDirectInput ? styles.dateSegment : undefined} />}</DateInput>;
    $[31] = allowDirectInput;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t) {
    t14 = t("aria.open_dates");
    $[33] = t;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== allowDirectInput) {
    t15 = allowDirectInput ? <CalendarIcon decorative={true} /> : <NavArrowDown decorative={true} />;
    $[35] = allowDirectInput;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t14 || $[38] !== t15) {
    t16 = <Button ariaLabel={t14} variant="icon">{t15}</Button>;
    $[37] = t14;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== handleGroupClick || $[41] !== t12 || $[42] !== t13 || $[43] !== t16) {
    t17 = <Group className={t12} onClick={handleGroupClick}>{t13}{t16}</Group>;
    $[40] = handleGroupClick;
    $[41] = t12;
    $[42] = t13;
    $[43] = t16;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== errorMessage) {
    t18 = errorMessage && <FieldError className={styles.fieldError}><WarningTriangle size="small" decorative={true} />{errorMessage}</FieldError>;
    $[45] = errorMessage;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  const t19 = !labelInside;
  let t20;
  if ($[47] !== t19) {
    t20 = clsx(styles.popover, {
      [styles.popoverOutside]: t19
    });
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== maxValue || $[50] !== minValue || $[51] !== showMonthYearDropdowns) {
    t21 = <Calendar showMonthYearDropdowns={showMonthYearDropdowns} minValue={minValue} maxValue={maxValue} />;
    $[49] = maxValue;
    $[50] = minValue;
    $[51] = showMonthYearDropdowns;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== children || $[54] !== t20 || $[55] !== t21) {
    t22 = <Popover className={t20}>{t21}{children}</Popover>;
    $[53] = children;
    $[54] = t20;
    $[55] = t21;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== isOpen || $[58] !== isRequired || $[59] !== maxValue || $[60] !== minValue || $[61] !== props || $[62] !== t10 || $[63] !== t17 || $[64] !== t18 || $[65] !== t22 || $[66] !== t6 || $[67] !== t7) {
    t23 = <AriaDatePicker {...props} isRequired={isRequired} minValue={minValue} maxValue={maxValue} isOpen={isOpen} onOpenChange={setIsOpen} className={t6}>{t7}{t10}{t17}{t18}{t22}</AriaDatePicker>;
    $[57] = isOpen;
    $[58] = isRequired;
    $[59] = maxValue;
    $[60] = minValue;
    $[61] = props;
    $[62] = t10;
    $[63] = t17;
    $[64] = t18;
    $[65] = t22;
    $[66] = t6;
    $[67] = t7;
    $[68] = t23;
  } else {
    t23 = $[68];
  }
  return t23;
}
const DrawerContext = createContext({
  close: () => {}
});
// Note: this may seem like unnecessary duplication, but component behaves differently as soon as we add Drawer, even if render logic does not render drawer.
function DatePickerWithDrawer(t0) {
  const $ = _c(74);
  let allowDirectInput;
  let children;
  let errorMessage;
  let isRequired;
  let label;
  let maxValue;
  let minValue;
  let props;
  let showMonthYearDropdowns;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      label,
      errorMessage,
      children,
      allowDirectInput,
      showMonthYearDropdowns,
      fullWidth: t1,
      labelInside: t2,
      isRequired,
      minValue,
      maxValue,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = allowDirectInput;
    $[2] = children;
    $[3] = errorMessage;
    $[4] = isRequired;
    $[5] = label;
    $[6] = maxValue;
    $[7] = minValue;
    $[8] = props;
    $[9] = showMonthYearDropdowns;
    $[10] = t1;
    $[11] = t2;
  } else {
    allowDirectInput = $[1];
    children = $[2];
    errorMessage = $[3];
    isRequired = $[4];
    label = $[5];
    maxValue = $[6];
    minValue = $[7];
    props = $[8];
    showMonthYearDropdowns = $[9];
    t1 = $[10];
    t2 = $[11];
  }
  const fullWidth = t1 === undefined ? true : t1;
  const labelInside = t2 === undefined ? true : t2;
  const t = useTranslations();
  const [open, setOpen] = useState(false);
  let t3;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setOpen(_temp);
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const buttonHandler = t3;
  let t4;
  if ($[13] !== allowDirectInput) {
    t4 = allowDirectInput ? makeGroupClickHandler(() => setOpen(true)) : undefined;
    $[13] = allowDirectInput;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const handleGroupClick = t4;
  const t5 = labelInside ? styles.datePicker : styles.datePickerOutside;
  const t6 = !fullWidth;
  let t7;
  if ($[15] !== t5 || $[16] !== t6) {
    t7 = clsx(t5, {
      [styles.fitContent]: t6
    });
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== allowDirectInput) {
    t8 = !allowDirectInput && <RAC_Button onPress={buttonHandler} className={styles.triggerArea} />;
    $[18] = allowDirectInput;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== label || $[21] !== t) {
    t9 = label ?? t("content.forms.start_date");
    $[20] = label;
    $[21] = t;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== isRequired || $[24] !== labelInside) {
    t10 = !labelInside && isRequired && <span className={styles.required} aria-hidden="true">{" "}*</span>;
    $[23] = isRequired;
    $[24] = labelInside;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t9) {
    t11 = <RAC_Label className={styles.label}>{t9}{t10}</RAC_Label>;
    $[26] = t10;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const t12 = !labelInside;
  let t13;
  if ($[29] !== allowDirectInput || $[30] !== t12) {
    t13 = clsx(styles.group, {
      [styles.groupBordered]: t12,
      [styles.groupClickable]: allowDirectInput
    });
    $[29] = allowDirectInput;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== allowDirectInput) {
    t14 = <DateInput className={styles.dateInput}>{segment => <DateSegment segment={segment} className={allowDirectInput ? styles.dateSegment : undefined} />}</DateInput>;
    $[32] = allowDirectInput;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t) {
    t15 = t("aria.open_dates");
    $[34] = t;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== allowDirectInput) {
    t16 = allowDirectInput ? <CalendarIcon decorative={true} /> : <PencilSquare decorative={true} />;
    $[36] = allowDirectInput;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t15 || $[39] !== t16) {
    t17 = <Button onPress={buttonHandler} ariaLabel={t15} variant="icon">{t16}</Button>;
    $[38] = t15;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== handleGroupClick || $[42] !== t13 || $[43] !== t14 || $[44] !== t17) {
    t18 = <Group className={t13} onClick={handleGroupClick}>{t14}{t17}</Group>;
    $[41] = handleGroupClick;
    $[42] = t13;
    $[43] = t14;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== errorMessage) {
    t19 = errorMessage && <FieldError className={styles.fieldError}><WarningTriangle size="small" decorative={true} />{errorMessage}</FieldError>;
    $[46] = errorMessage;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== t) {
    t20 = t("aria.date_picker");
    $[48] = t;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      close: buttonHandler
    };
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = () => setOpen(false);
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== maxValue || $[53] !== minValue || $[54] !== showMonthYearDropdowns) {
    t23 = <Calendar showMonthYearDropdowns={showMonthYearDropdowns} minValue={minValue} maxValue={maxValue} onChange={t22} />;
    $[52] = maxValue;
    $[53] = minValue;
    $[54] = showMonthYearDropdowns;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== children || $[57] !== t23) {
    t24 = <DrawerContext.Provider value={t21}>{t23}{children}</DrawerContext.Provider>;
    $[56] = children;
    $[57] = t23;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== open || $[60] !== t20 || $[61] !== t24) {
    t25 = <Drawer isOpen={open} close={buttonHandler} ariaLabel={t20}>{t24}</Drawer>;
    $[59] = open;
    $[60] = t20;
    $[61] = t24;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] !== isRequired || $[64] !== maxValue || $[65] !== minValue || $[66] !== props || $[67] !== t11 || $[68] !== t18 || $[69] !== t19 || $[70] !== t25 || $[71] !== t7 || $[72] !== t8) {
    t26 = <AriaDatePicker {...props} isRequired={isRequired} minValue={minValue} maxValue={maxValue} className={t7}>{t8}{t11}{t18}{t19}{t25}</AriaDatePicker>;
    $[63] = isRequired;
    $[64] = maxValue;
    $[65] = minValue;
    $[66] = props;
    $[67] = t11;
    $[68] = t18;
    $[69] = t19;
    $[70] = t25;
    $[71] = t7;
    $[72] = t8;
    $[73] = t26;
  } else {
    t26 = $[73];
  }
  return t26;
}
function _temp(prev) {
  return !prev;
}
function DatePickerDesktop(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <div className={styles.desktopOnly}><DatePicker {...props} /></div>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function DatePickerMobile(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <div className={styles.mobileOnly}><DatePickerWithDrawer {...props} /></div>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export { DatePickerMobile, DatePicker, DatePickerDesktop, DrawerContext };